/**
 * apps/web/src/lib/money.ts — the ONLY money formatter in the web app.
 *
 * Spec: 14-theme.md §6 (the rendering contract), 13-ui-screens.md §3.3.
 *
 * NON-NEGOTIABLES
 *  1. Money arrives from the API as a decimal STRING at `numeric(14,4)` scale,
 *     trailing zeros preserved (`"144.0000"`). It is NEVER a JS number: no
 *     `Number()`, no `parseFloat()`, no unary `+`, no `.toLocaleString()`.
 *  2. `Decimal.set()` runs exactly ONCE in the browser bundle, and it runs
 *     here. `packages/shared/src/money.ts` uses a separate `Decimal.clone()`,
 *     so the two environments never share a global.
 *  3. The sign test is `.lessThan(0)`, NOT `.isNegative()`. decimal.js has a
 *     signed zero, so `-0.0000` (which the allocator can legitimately produce)
 *     would otherwise render as a red `(RM 0.00)`.
 */
import Decimal from 'decimal.js';

/* Configure once, at module load. 34 significant digits comfortably covers
 * numeric(14,4). Wide exponent bounds stop toString() ever producing "1e+21". */
Decimal.set({
  precision: 34,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -18,
  toExpPos: 40,
});

/** U+00A0 — the amount must never wrap away from its `RM` unit. */
const NBSP = ' ';
const EM_DASH = '—';
/** Insert a comma every 3 digits, right to left, integer part only. */
const GROUP = /\B(?=(\d{3})+(?!\d))/g;

/** A money / quantity / factor value as it arrives on the wire. */
export type Money = string | null | undefined;

/**
 * `currency: false` drops the `RM` prefix and changes NOTHING else — grouping,
 * the two decimals and the parentheses for a negative all stay (§6.2). It exists
 * for one shape of screen: a grid whose every cell is money and which states
 * the unit once in its caption, the way the client's own workbook does. The
 * daily earnings report (FR-7.5) has 31 day columns a row, and `RM 1,234.56`
 * repeated 31 times is the same unit 31 times over and a figure that does not
 * fit a day column. A lone figure on a form or a stat card keeps its prefix.
 */
export interface FormatMoneyOptions {
  currency?: boolean;
}

function unit(opts?: FormatMoneyOptions): string {
  return opts?.currency === false ? '' : `RM${NBSP}`;
}

function group(intPart: string): string {
  return intPart.replace(GROUP, ',');
}

function isAbsent(raw: Money): raw is null | undefined | '' {
  return raw === null || raw === undefined || raw === '';
}

/**
 * The one sign test. `.lessThan(0)` and never `.isNegative()`:
 * `new Decimal('-0.0000').isNegative() === true`, which would paint a genuine
 * zero red and wrap it in parentheses.
 */
export function isNegative(raw: Money): boolean {
  if (isAbsent(raw)) return false;
  return new Decimal(raw).lessThan(0);
}

/** True for a real zero. `RM 0.00` and `—` are different states (§6.1 rule 5). */
export function isZero(raw: Money): boolean {
  if (isAbsent(raw)) return false;
  return new Decimal(raw).isZero();
}

/**
 * True for a value strictly greater than zero. Absent is NOT positive.
 *
 * Exists so a screen never writes `Number(amount) > 0`. That looks harmless on a
 * greater-than-zero test and is the thin end of the wedge: the same habit two lines
 * later turns a comparison into float arithmetic, and this file's rule 1 has no
 * exemption for "only an advisory".
 */
export function isPositive(raw: Money): boolean {
  if (isAbsent(raw)) return false;
  return new Decimal(raw).greaterThan(0);
}

/**
 * `a > b`, exactly. Absent on either side is `false` — there is nothing to compare.
 *
 * The reason this is not `Number(a) > Number(b)`: `0.1 + 0.7` is `0.7999999999999999`
 * in binary floating point, so a boundary that ought to be exactly equal compares as
 * greater and a screen warns about a claim that fits.
 */
export function exceeds(a: Money, b: Money): boolean {
  if (isAbsent(a) || isAbsent(b)) return false;
  return new Decimal(a).greaterThan(new Decimal(b));
}

/**
 * `RM 1,234.56` · `(RM 1.50)` · `—` — or `1,234.56` · `(1.50)` with
 * `{ currency: false }`, see `FormatMoneyOptions`.
 * Rounds the 4 dp wire value to 2 dp for display with ROUND_HALF_UP (half away
 * from zero, matching the engine's `round2`). Never truncates. Always exactly
 * two decimals. The minus sign is never printed — parentheses replace it.
 */
export function formatMoney(raw: Money, opts?: FormatMoneyOptions): string {
  if (isAbsent(raw)) return EM_DASH;
  const d = new Decimal(raw);
  const fixed = d.abs().toFixed(2, Decimal.ROUND_HALF_UP); // "1234.56"
  const [int = '0', frac = '00'] = fixed.split('.');
  const body = `${unit(opts)}${group(int)}.${frac}`;
  return d.lessThan(0) ? `(${body})` : body;
}

/** `48` · `1,250.5` · `0.125` — no currency, ≤ 3 dp, trailing zeros trimmed. */
export function formatQty(raw: Money): string {
  if (isAbsent(raw)) return EM_DASH;
  const s = new Decimal(raw).toDecimalPlaces(3, Decimal.ROUND_HALF_UP).toFixed();
  const [int = '0', frac] = s.split('.');
  const neg = int.startsWith('-');
  const body = frac ? `${group(neg ? int.slice(1) : int)}.${frac}` : group(neg ? int.slice(1) : int);
  return neg ? `-${body}` : body;
}

/** `RM 12.00` · `RM 0.8025` — min 2 dp, max 6 dp, trailing zeros beyond 2 trimmed. */
export function formatRate(raw: Money, opts?: FormatMoneyOptions): string {
  if (isAbsent(raw)) return EM_DASH;
  const d = new Decimal(raw);
  let s = d.abs().toFixed(6, Decimal.ROUND_HALF_UP); // "12.000000"
  s = s.replace(/(\.\d{2}\d*?)0+$/, '$1'); // trim beyond 2 dp
  const [int = '0', frac = '00'] = s.split('.');
  const body = `${unit(opts)}${group(int)}.${frac}`;
  return d.lessThan(0) ? `(${body})` : body;
}

/** `+12.4%` · `-3.1%` — signed, one decimal. Reconciliation variance. */
export function formatPct(raw: Money): string {
  if (isAbsent(raw)) return EM_DASH;
  const d = new Decimal(raw);
  const sign = d.lessThan(0) ? '-' : '+';
  return `${sign}${d.abs().toFixed(1)}%`;
}

/** `1.500000` → `×1.5` — a multiplier chip (casual timesheet). */
export function formatFactor(raw: Money): string {
  if (isAbsent(raw)) return EM_DASH;
  const s = new Decimal(raw).toFixed(6);
  return `×${s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')}`;
}

/** `3.5 h` — hours from a decimal-hours string (§3.3). */
export function formatHours(raw: Money): string {
  if (isAbsent(raw)) return EM_DASH;
  return `${new Decimal(raw).toFixed(1)}${NBSP}h`;
}

/**
 * `210` minutes → `3.5 h`. Durations are the ONLY numeric field the API sends
 * as a JSON number (12-api-contract.md §1.3), so a `number` here is correct.
 */
export function formatMinutesAsHours(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return EM_DASH;
  return `${new Decimal(minutes).div(60).toFixed(1)}${NBSP}h`;
}

/** Sum a column of wire strings without ever leaving fixed point. */
export function sumMoney(values: readonly Money[]): string {
  return values
    .reduce<Decimal>((acc, v) => (isAbsent(v) ? acc : acc.plus(new Decimal(v))), new Decimal(0))
    .toFixed(4);
}

/** The em dash used for an absent figure — exported so cells can match it. */
export const ABSENT_DASH = EM_DASH;
